import React, { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate, useLocation, Link } from "react-router-dom"
import { Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import SoldxHeader from "../../../components/SoldxHeader"
import BidRequestModal from "./BidRequestModal"
import BrokerBidBlockedModal from "../../../components/BrokerBidBlockedModal/BrokerBidBlockedModal"
import apiClient, { API_BASE } from "helpers/apiClient"
import { decodeDraftRawToReact } from "helpers/draftRawToReact"
import { getPropertyDetailGateReason } from "helpers/auth_api"
import { formatCategoryFieldDisplay, formatDisplayNumber } from "helpers/indianNumber"
import { formatIndianDate, formatIndianDateTime } from "helpers/indianDate"
import {
  isBrokerBidBlocked,
  buildBrokerBidListingContext,
  getBrokerBidBlockedMessage,
  getAuthUserId,
  resolveListingCreatorId,
} from "helpers/biddingAccess"
import SoldxBreadcrumb from "../../../components/SoldxBreadcrumb"
import SoldxLandingFooter from "../../../components/SoldxLandingFooter"

type PropertyTab = "description" | "details" | "dataroom"

const LISTING_GET_BY_ID_URL = "/api/listings/getListingById"
const BIDS_DETAILS_URL = "/api/bids/listing"
const BIDS_MY_BIDS_URL = "/api/bids/my-bids"

interface BiddingDetailsData {
  currentHighestBid: { amount: number } | null
  minNextBid: number
  listing?: {
    canBrokerBid?: string | null
    isExclusiveMandate?: string | null
    listingCreatorId?: string | null
    listingCreatorRole?: string | null
    biddingMinIncrement?: number
  }
}

interface ApiListing {
  _id: string
  propertyTitle?: string
  address?: string
  city?: string
  state?: string
  pinCode?: string
  category?: string
  status?: string
  propertyImagesUrls?: string[]
  totalSalePrice?: number
  pricePerSqFt?: number
  superAreaSqFt?: number
  carpetAreaSqFt?: number
  totalCarpetAreaSqFt?: number
  auctionStatus?: "live" | "upcoming" | "ended"
  isUpcoming?: boolean
  auctionStartDate?: string
  auctionEndDate?: string
  biddingStartPrice?: number
  reservePrice?: number
  biddingMinIncrement?: number
  biddingEnabled?: boolean
  floorNumber?: number
  totalFloorsInBuilding?: number
  unitNo?: string
  constructionStatus?: string
  furnishingStatus?: string
  microMarketLocality?: string
  propertyDocumentsUrls?: string[]
  propertyTaxReceiptsUrls?: string[]
  legalDocumentsUrls?: string[]
  /** Root-level document URLs (used when API does not send aggregated arrays) */
  floorPlanUrl?: string
  floorPlanSiteLayoutUrl?: string
  buildingBrochureUrl?: string
  /** Auto-generated flyer PDF when listing is upcoming/live */
  generatedFlyerPdfUrl?: string
  dataRoomUrls?: string[]
  titleDeedUrl?: string
  titleDeedConveyanceDeedUrl?: string
  titleDeedSaleDeedUrl?: string
  occupancyCertificateOcUrl?: string
  possessionCertificateUrl?: string
  encumbranceCertificateUrl?: string
  leaseAgreementUrl?: string
  vastuReportUrl?: string
  latestTaxReceiptsUrl?: string
  categorySpecific?: {
    superArea?: string
    carpetAreaInputMethod?: string
    admin_propertyDescription?: string
    admin_shortPropertyDescription?: string
    floorNumber?: string
    totalFloorsInBuilding?: string
    admin_propertyVideoUrl?: string
    videoTourLink?: string
    [key: string]: unknown
  }
  /** User who created the listing (broker/seller) – cannot bid on own listing */
  createdBy?: string | { _id: string; role?: string }
}

function formatPrice(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—"
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)} Cr`
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} Lakh`
  return `₹${n.toLocaleString("en-IN")}`
}

function formatDateTime(iso: string | undefined): string {
  return formatIndianDateTime(iso)
}

function pickUserIdFromAuthStorage(): string | null {
  try {
    const raw = localStorage.getItem("authUser")
    if (!raw) return null
    const obj = JSON.parse(raw) as Record<string, unknown>
    const v = obj._id ?? obj.uid ?? obj.id ?? obj.userId
    if (v == null) return null
    if (typeof v === "object" && v !== null && "_id" in (v as object)) {
      return String((v as { _id: unknown })._id)
    }
    return String(v)
  } catch {
    return null
  }
}

/** Get YouTube or Vimeo embed URL from share/watch URL, or null if not supported. */
function getVideoEmbedUrl(url: string | undefined): string | null {
  if (!url || typeof url !== "string") return null
  const u = url.trim()
  if (!u) return null
  try {
    // Already embed URLs – pass through with https
    if (u.includes("youtube.com/embed/")) {
      const m = u.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/)
      return m ? `https://www.youtube.com/embed/${m[1]}` : null
    }
    if (u.includes("player.vimeo.com/video/")) {
      const m = u.match(/player\.vimeo\.com\/video\/(\d+)/)
      return m ? `https://player.vimeo.com/video/${m[1]}` : null
    }
    // Watch/share/shorts URLs
    if (u.includes("youtube.com") || u.includes("youtu.be")) {
      const m = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
      return m ? `https://www.youtube.com/embed/${m[1]}` : null
    }
    if (u.includes("vimeo.com")) {
      const m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/)
      return m ? `https://player.vimeo.com/video/${m[1]}` : null
    }
  } catch {
    return null
  }
  return null
}

function useAuctionCountdown(endDateIso: string | undefined) {
  const [left, setLeft] = useState<{ days: number; hours: number; mins: number; secs: number } | null>(null)

  useEffect(() => {
    if (!endDateIso) {
      setLeft(null)
      return
    }
    const end = new Date(endDateIso).getTime()
    const tick = () => {
      const now = Date.now()
      if (end <= now) {
        setLeft({ days: 0, hours: 0, mins: 0, secs: 0 })
        return
      }
      const d = Math.floor((end - now) / (1000 * 60 * 60 * 24))
      const h = Math.floor(((end - now) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const m = Math.floor(((end - now) % (1000 * 60 * 60)) / (1000 * 60))
      const s = Math.floor(((end - now) % (1000 * 60)) / 1000)
      setLeft({ days: d, hours: h, mins: m, secs: s })
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [endDateIso])

  return left
}

const PropertyDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [listing, setListing] = useState<ApiListing | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [bidModalOpen, setBidModalOpen] = useState(false)
  const [biddingDetails, setBiddingDetails] = useState<BiddingDetailsData | null>(null)
  const [userHasBid, setUserHasBid] = useState(false)
  const [userLastBidAmount, setUserLastBidAmount] = useState<number | null>(null)
  const [initialBidForModal, setInitialBidForModal] = useState("")
  const [brokerBidBlockedModalOpen, setBrokerBidBlockedModalOpen] = useState(false)
  const [brokerBidBlockedMessage, setBrokerBidBlockedMessage] = useState("")
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null)
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)
  const [activeTab, setActiveTab] = useState<PropertyTab>("description")

  const propertyGate = getPropertyDetailGateReason()
  const isLoggedIn = !!localStorage.getItem("authUser")

  const handleTabClick = (tab: PropertyTab) => {
    if ((tab === "details" || tab === "dataroom") && !isLoggedIn) {
      setActiveTab(tab)
    } else {
      setActiveTab(tab)
    }
  }

  useEffect(() => {
    setSelectedMediaIndex(0)
  }, [id])

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }
    if (getPropertyDetailGateReason()) {
      setListing(null)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    apiClient
      .get<{ success: boolean; data?: ApiListing }>(`${LISTING_GET_BY_ID_URL}/${id}`)
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          setListing(res.data.data)
        } else {
          setListing(null)
        }
      })
      .catch(() => {
        setError("Failed to load property.")
        setListing(null)
      })
      .finally(() => setLoading(false))
  }, [id])

  // Fetch bidding details and whether current user has already bid (when logged in and bidding is possible)
  useEffect(() => {
    if (!id || !listing?.biddingEnabled) {
      setBiddingDetails(null)
      setUserHasBid(false)
      return
    }
    const isLiveOrUpcoming = listing.auctionStatus === "live" || listing.isUpcoming
    if (!isLiveOrUpcoming) {
      setBiddingDetails(null)
      setUserHasBid(false)
      return
    }
    apiClient
      .get<{ success: boolean; data?: BiddingDetailsData }>(`${BIDS_DETAILS_URL}/${id}`)
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          setBiddingDetails(res.data.data)
        } else {
          setBiddingDetails(null)
        }
      })
      .catch(() => setBiddingDetails(null))

    const isLoggedIn = !!localStorage.getItem("authUser")
    if (!isLoggedIn) {
      setUserHasBid(false)
      setUserLastBidAmount(null)
      return
    }
    apiClient
      .get<{ success: boolean; data?: { _id: string; amount?: number }[] }>(`${BIDS_MY_BIDS_URL}?listingId=${id}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data?.data) && res.data.data.length > 0) {
          setUserHasBid(true)
          // Get the latest bid (highest amount or last in array)
          const bids = res.data.data
          const lastBid = bids.reduce((max, bid) =>
            (bid.amount ?? 0) > (max.amount ?? 0) ? bid : max
            , bids[0])
          setUserLastBidAmount(lastBid.amount ?? null)
        } else {
          setUserHasBid(false)
          setUserLastBidAmount(null)
        }
      })
      .catch(() => {
        setUserHasBid(false)
        setUserLastBidAmount(null)
      })
  }, [id, listing?.biddingEnabled, listing?.auctionStatus, listing?.isUpcoming])

  const isLive = listing?.auctionStatus === "live"
  const isUpcoming = !!listing?.isUpcoming || listing?.auctionStatus === "upcoming"
  const countdownTarget = isLive ? listing?.auctionEndDate : isUpcoming ? listing?.auctionStartDate : undefined
  const countdown = useAuctionCountdown(countdownTarget)
  const countdownLabel = isLive ? "Time left" : isUpcoming ? "Starts in" : "Time left"
  const statusLabel = isLive ? "Live" : isUpcoming ? "Upcoming" : listing?.auctionStatus === "ended" ? "Ended" : "—"
  const currentUserId = pickUserIdFromAuthStorage()
  const listingCreatorId = resolveListingCreatorId(listing?.createdBy)
  const isOwnListing = !!(
    currentUserId &&
    listingCreatorId &&
    String(currentUserId).trim() === String(listingCreatorId).trim()
  )
  const canBid = listing?.biddingEnabled && listing.auctionStatus === "live" && !isOwnListing
  const showPlaceBidButton = listing?.biddingEnabled && (listing.auctionStatus === "live" || listing.auctionStatus === "upcoming" || listing.isUpcoming)

  const handlePlaceBidClick = () => {
    const isLoggedIn = !!localStorage.getItem("authUser")
    if (!isLoggedIn) {
      navigate("/login", { state: { from: location }, replace: false })
      return
    }
    const brokerBidCtx = buildBrokerBidListingContext({
      canBrokerBid:
        biddingDetails?.listing?.canBrokerBid ??
        listing?.categorySpecific?.admin_canBrokerBid,
      isExclusiveMandate:
        biddingDetails?.listing?.isExclusiveMandate ??
        listing?.categorySpecific?.admin_isExclusiveMandate,
      createdBy: {
        _id:
          resolveListingCreatorId(listing?.createdBy) ??
          biddingDetails?.listing?.listingCreatorId ??
          undefined,
        role:
          (listing?.createdBy && typeof listing.createdBy === "object"
            ? listing.createdBy.role
            : undefined) ?? biddingDetails?.listing?.listingCreatorRole,
      },
      currentUserId: getAuthUserId(),
    })
    if (isBrokerBidBlocked(brokerBidCtx)) {
      setBrokerBidBlockedMessage(getBrokerBidBlockedMessage(brokerBidCtx))
      setBrokerBidBlockedModalOpen(true)
      return
    }
    setBrokerBidBlockedModalOpen(false)
    if (userHasBid && biddingDetails) {
      setInitialBidForModal(String(biddingDetails.minNextBid))
    } else {
      setInitialBidForModal(listing?.biddingStartPrice != null ? String(listing.biddingStartPrice) : "0")
    }
    setBidModalOpen(true)
  }

  type GalleryItem = { type: "image"; url: string } | { type: "video"; embedUrl: string }
  const galleryMedia = useMemo((): GalleryItem[] => {
    if (!listing) return []
    const urls = listing.propertyImagesUrls ?? []
    const videoUrlVal = (listing.categorySpecific?.admin_propertyVideoUrl ?? listing.categorySpecific?.videoTourLink) as string | undefined
    const embedUrl = getVideoEmbedUrl(videoUrlVal)
    const items: GalleryItem[] = []
    urls.forEach((path) => items.push({ type: "image", url: `${API_BASE}${path}` }))
    if (embedUrl) items.push({ type: "video", embedUrl })
    return items
  }, [listing])

  if (!id) {
    return (
      <div className="soldx-landing">
        <SoldxHeader solid />
        <main className="container py-5 text-center">
          <h2>Invalid property</h2>
          <button className="btn btn-primary mt-3" onClick={() => navigate("/properties")}>
            <i className="mdi mdi-arrow-left" /> Back to Properties
          </button>
        </main>

        <SoldxLandingFooter />
      </div>
    )
  }

  if (propertyGate) {
    const isLoginGate = propertyGate === "login"
    const isPendingGate = propertyGate === "pending"
    const kycIcon = isLoginGate ? "mdi-lock-outline" : isPendingGate ? "mdi-clock-outline" : "mdi-shield-alert"
    return (
      <div className="soldx-landing">
        <SoldxHeader solid />
        <main className="container py-5">
          <div className="border rounded-3 p-4 p-md-5 mx-auto text-center" style={{ maxWidth: 520 }}>
            <i className={`mdi ${kycIcon} text-primary`} style={{ fontSize: 48 }} aria-hidden />
            <h1 className="h4 mt-3 mb-2">
              {isLoginGate
                ? "Sign in to view this property"
                : isPendingGate
                  ? "KYC pending admin approval"
                  : "Please complete your KYC first"}
            </h1>
            <p className="text-muted mb-4">
              {isLoginGate
                ? "Log in or create an account to see full property details, photos, and documents."
                : isPendingGate
                  ? "Your KYC documents are submitted. An administrator must approve your account before you can view this property or use bidding."
                  : "Complete KYC in your profile, then wait for an administrator to approve your account before you can view full property details."}
            </p>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {isLoginGate ? (
                <>
                  <Link to="/login" state={{ from: location }} className="btn btn-primary">
                    Log in
                  </Link>
                  <Link to="/register" className="btn btn-outline-primary">
                    Register
                  </Link>
                </>
              ) : isPendingGate ? (
                <Link to="/profile" className="btn btn-primary">
                  View profile status
                </Link>
              ) : (
                <Link to="/profile" className="btn btn-primary">
                  Complete your KYC
                </Link>
              )}
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/properties")}>
                <i className="mdi mdi-arrow-left me-1" />
                Back to Properties
              </button>
            </div>
          </div>
        </main>

        <SoldxLandingFooter />
      </div>
    )
  }

  if (loading) {
    return (
      <div className="soldx-landing">
        <SoldxHeader solid />
        <main className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted">Loading property...</p>
        </main>

        <SoldxLandingFooter />
      </div>
    )
  }

  if (error || !listing) {
    return (
      <div className="soldx-landing">
        <SoldxHeader solid />
        <main className="container py-5 text-center">
          <h2>Property not found</h2>
          {error && <p className="text-muted">{error}</p>}
          <button className="btn btn-primary mt-3" onClick={() => navigate("/properties")}>
            <i className="mdi mdi-arrow-left" /> Back to Properties
          </button>
        </main>

        <SoldxLandingFooter />
      </div>
    )
  }

  const imgUrl = listing.propertyImagesUrls?.[0]
  const imgSrc = imgUrl ? `${API_BASE}${imgUrl}` : "https://images.unsplash.com/photo-1554435493-93422e8220c8?w=800&h=500&fit=crop"
  const description = listing.categorySpecific?.admin_propertyDescription ?? listing.categorySpecific?.admin_shortPropertyDescription ?? ""
  // const videoUrl = (listing.categorySpecific?.admin_propertyVideoUrl ?? listing.categorySpecific?.videoTourLink) as string | undefined

  return (
    <div className="soldx-landing">
      <SoldxHeader solid />

      <main className="container py-5">
        <SoldxBreadcrumb variant="page" heroTone="light" className="mb-5" />
        <button className="btn btn-outline-primary mb-4" onClick={() => navigate("/properties")}>
          <i className="mdi mdi-arrow-left" /> Back to Properties
        </button>



        <div className="property-modal-content">
          <div className="row mb-4">
            <div className="col-lg-7">
              {/* Flipkart-style: one main image/video + thumbnail strip */}
              <div className="property-detail-hero-wrapper position-relative rounded-3 p-2 py-3 overflow-hidden border">
                <span className={`property-status property-status--${statusLabel.toLowerCase().replace(/\s+/g, "-")}`}>
                  {statusLabel}
                </span>
                <div className="property-detail-hero">
                  {galleryMedia.length === 0 ? (
                    <button type="button" className="property-detail-hero-main w-100 h-100 border-0 p-0" onClick={() => setLightboxImageUrl(imgSrc)}>
                      <img src={imgSrc} alt={listing.propertyTitle || "Property"} className="property-detail-hero-img" />
                      <span className="property-detail-hero-zoom"><i className="mdi mdi-magnify-plus" /></span>
                    </button>
                  ) : (() => {
                    const idx = Math.min(selectedMediaIndex, galleryMedia.length - 1)
                    const current = galleryMedia[idx]
                    if (!current) return null
                    if (current.type === "image")
                      return (
                        <button type="button" className="property-detail-hero-main w-100 h-100 border-0 p-0" onClick={() => setLightboxImageUrl(current.url)}>
                          <img src={current.url} alt={listing.propertyTitle || "Property"} className="property-detail-hero-img" />
                          <span className="property-detail-hero-zoom"><i className="mdi mdi-magnify-plus" /></span>
                        </button>
                      )
                    const videoSrc = `${current.embedUrl}${current.embedUrl.includes("?") ? "&" : "?"}autoplay=1&mute=1`
                    return (
                      <div className="property-detail-hero-main property-detail-hero-main--video w-100 ratio ratio-16x9">
                        <iframe src={videoSrc} title="Property video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ border: 0 }} />
                      </div>
                    )
                  })()}
                </div>
                <div className="property-detail-thumbs">
                  {galleryMedia.length === 0 ? (
                    <button type="button" className="property-detail-thumb property-detail-thumb--active" onClick={() => { }}>
                      <img src={imgSrc} alt="" />
                    </button>
                  ) : (
                    galleryMedia.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`property-detail-thumb ${idx === Math.min(selectedMediaIndex, galleryMedia.length - 1) ? "property-detail-thumb--active" : ""}`}
                        onClick={() => setSelectedMediaIndex(idx)}
                        aria-label={`View ${idx + 1}`}
                      >
                        {item.type === "image" ? (
                          <img src={item.url} alt="" />
                        ) : (
                          <span className="property-detail-thumb-video">
                            <i className="mdi mdi-play-circle" />
                            <span className="property-detail-thumb-video-label">Video</span>
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="property-detail-info mb-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge bg-primary">{formatDisplayNumber(listing.categorySpecific?.superArea ?? listing.superAreaSqFt)} Sq.ft</span>
                  <span className="badge bg-primary">{listing.category || "—"}</span>
                  <span className="badge bg-secondary">{listing.status || "—"}</span>
                </div>
                <h4 className="mb-2">{listing.propertyTitle || "—"}</h4>
                <p className="text-muted mb-3">
                  <i className="mdi mdi-map-marker text-danger" /> {listing.address || [listing.city, listing.state].filter(Boolean).join(", ") || "—"}
                  <br />
                  {listing.pinCode && <small>Pincode: {listing.pinCode}</small>}
                </p>

                <div className="property-detail-price p-3 rounded mb-3">
                  <div className="row align-items-center">
                    <div className="col-6">
                      <small className="text-muted d-block">Price</small>
                      <span className="h4 text-primary mb-0">{formatPrice(listing.biddingStartPrice)}</span>
                    </div>
                    <div className="col-6 text-end">
                      <small className="text-muted d-block">Price/sq.ft</small>
                      <span className="h6 mb-0">
                        {listing.pricePerSqFt != null ? `₹${Number(listing.pricePerSqFt).toLocaleString("en-IN")}` : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="property-auction-box p-3 bg-light rounded mb-3">
                  <h6 className="mb-3"><i className="mdi mdi-gavel" /> Auction Details</h6>
                  <div className="row g-2 text-center">
                    {/* <div className="col-6">
                      <small className="text-muted d-block">Min Increment</small>
                      <strong>{listing.biddingMinIncrement != null ? formatPrice(listing.biddingMinIncrement) : "—"}</strong>
                    </div> */}
                    <div className="col-6">
                      <small className="text-muted d-block">Auction Start</small>
                      <strong className="small">{formatDateTime(listing.auctionStartDate)}</strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Auction End</small>
                      <strong className="small">{formatDateTime(listing.auctionEndDate)}</strong>
                    </div>
                  </div>
                  {(isLive || isUpcoming) && countdown && (
                    <div className="text-center mt-2 pt-2 border-top">
                      <small className="text-muted d-block">{countdownLabel}</small>
                      <span className="badge bg-light text-dark border px-2 py-2 me-1">
                        {String(countdown.days).padStart(2, "0")}d
                      </span>
                      <span className="badge bg-light text-dark border px-2 py-2 me-1">
                        {String(countdown.hours).padStart(2, "0")}h
                      </span>
                      <span className="badge bg-light text-dark border px-2 py-2 me-1">
                        {String(countdown.mins).padStart(2, "0")}m
                      </span>
                      <span className="badge bg-light text-dark border px-2 py-2">
                        {String(countdown.secs).padStart(2, "0")}s
                      </span>
                    </div>
                  )}

                </div>

                {(isLive || isUpcoming) && (listing.generatedFlyerPdfUrl || listing.buildingBrochureUrl) && (
                  <a
                    href={`${API_BASE}${listing.generatedFlyerPdfUrl || listing.buildingBrochureUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-danger w-100 mb-2"
                    download
                  >
                    <i className="mdi mdi-file-pdf-box me-1" /> Download Flyer (PDF)
                  </a>
                )}

                {showPlaceBidButton && !userHasBid && (
                  <button
                    type="button"
                    className="btn btn-primary btn-lg w-100 mb-2"
                    disabled={!canBid}
                    title={isOwnListing ? "You cannot bid on your own property" : !canBid ? "Bidding opens when auction goes live" : undefined}
                    onClick={() => canBid && handlePlaceBidClick()}
                  >
                    <i className="mdi mdi-gavel" /> Place Bid
                  </button>
                )}
                {showPlaceBidButton && isOwnListing && (
                  <p className="text-muted small mb-2 mt-1 text-center">
                    You cannot bid on your own property.
                  </p>
                )}
                {canBid && userHasBid && (() => {
                  const currentHighest = biddingDetails?.currentHighestBid?.amount ?? 0
                  const isHighestBidder = userLastBidAmount != null && userLastBidAmount >= currentHighest

                  return (
                    <div className="property-request-new-bid p-3 bg-light rounded border">
                      <h6 className="mb-3 d-flex align-items-center justify-content-between">
                        <span><i className="mdi mdi-gavel me-2 text-primary" />Your Bidding Status</span>
                        {isHighestBidder ? (
                          <span className="badge bg-success"><i className="mdi mdi-check-circle me-1" />Highest Bidder</span>
                        ) : (
                          <span className="badge bg-warning text-dark"><i className="mdi mdi-alert me-1" />Outbid</span>
                        )}
                      </h6>

                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <div className="p-2 bg-white rounded border text-center">
                            <small className="text-muted d-block">Current Highest Bid</small>
                            <strong className="text-primary fs-6">
                              {!biddingDetails
                                ? "…"
                                : biddingDetails.currentHighestBid
                                  ? `₹${Number(biddingDetails.currentHighestBid.amount).toLocaleString("en-IN")}`
                                  : "No bids yet"}
                            </strong>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="p-2 bg-white rounded border text-center">
                            <small className="text-muted d-block">Your Last Bid</small>
                            <strong className={`fs-6 ${isHighestBidder ? "text-success" : "text-secondary"}`}>
                              {userLastBidAmount != null && Number.isFinite(userLastBidAmount)
                                ? `₹${Number(userLastBidAmount).toLocaleString("en-IN")}`
                                : "—"}
                            </strong>
                          </div>
                        </div>

                      </div>

                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={() => handlePlaceBidClick()}
                      >
                        <i className="mdi mdi-gavel me-2" />
                        {isHighestBidder ? "Increase Your Bid" : "Place Higher Bid"}
                      </button>
                    </div>
                  )
                })()}
                {listing.auctionStatus === "ended" && (
                  <button className="btn btn-secondary btn-lg w-100 mb-2" disabled>
                    Auction Ended
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Property Tabs */}
          <div className="property-tabs-container mb-4">
            <Nav tabs className="property-detail-tabs">
              <NavItem>
                <NavLink
                  className={activeTab === "description" ? "active" : ""}
                  onClick={() => handleTabClick("description")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="mdi mdi-text-box-outline me-1" /> Property Description
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === "details" ? "active" : ""}
                  onClick={() => handleTabClick("details")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="mdi mdi-clipboard-list-outline me-1" /> Property Details
                  {!isLoggedIn && <i className="mdi mdi-lock ms-1 text-warning" />}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === "dataroom" ? "active" : ""}
                  onClick={() => handleTabClick("dataroom")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="mdi mdi-folder-open me-1" /> Data Room
                  {!isLoggedIn && <i className="mdi mdi-lock ms-1 text-warning" />}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="pt-4">
              {/* Tab 1: Property Description - No login required */}
              <TabPane tabId="description">
                {description ? (
                  <div className="card">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0"><i className="mdi mdi-information" /> Description</h6>
                    </div>
                    <div className="card-body">
                      {decodeDraftRawToReact(description)}
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info">
                    <i className="mdi mdi-information-outline me-2" />
                    No description available for this property.
                  </div>
                )}
              </TabPane>

              {/* Tab 2: Property Details - Login required */}
              <TabPane tabId="details">
                {!isLoggedIn ? (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i className="mdi mdi-lock-outline text-warning" style={{ fontSize: "4rem" }} />
                      <h5 className="mt-3 mb-2">Login Required</h5>
                      <p className="text-muted mb-4">Please login to see the property details.</p>
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={() => navigate("/login", { state: { from: location }, replace: false })}
                      >
                        <i className="mdi mdi-login me-2" /> Login to View Details
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {/* <div className="col-md-6 mb-4">
                      <div className="card h-100">
                        <div className="card-header bg-dark text-white">
                          <h6 className="mb-0"><i className="mdi mdi-ruler" /> Property Specifications</h6>
                        </div>
                        <div className="card-body">
                          <table className="table table-sm table-borderless mb-0">
                            <tbody>
                              {((listing.categorySpecific?.superArea != null && String(listing.categorySpecific.superArea).trim() !== "") || listing.superAreaSqFt != null) ? (
                                <tr><td className="text-muted">Super Area</td><td className="text-end"><strong>{listing.categorySpecific?.superArea ?? listing.superAreaSqFt ?? "—"}</strong></td></tr>
                              ) : null}
                              {(listing.carpetAreaSqFt != null || (listing.categorySpecific?.carpetAreaInputMethod != null && String(listing.categorySpecific.carpetAreaInputMethod).trim() !== "")) ? (
                                <tr><td className="text-muted">Carpet Area</td><td className="text-end"><strong>{listing.carpetAreaSqFt ?? listing.categorySpecific?.carpetAreaInputMethod ?? "—"}</strong></td></tr>
                              ) : null}
                              {(listing.floorNumber != null || (listing.categorySpecific?.floorNumber != null && String(listing.categorySpecific.floorNumber).trim() !== "")) ? (
                                <tr><td className="text-muted">Floor</td><td className="text-end"><strong>{listing.floorNumber ?? listing.categorySpecific?.floorNumber ?? "—"}</strong></td></tr>
                              ) : null}
                              {(listing.totalFloorsInBuilding != null || (listing.categorySpecific?.totalFloorsInBuilding != null && String(listing.categorySpecific.totalFloorsInBuilding).trim() !== "")) ? (
                                <tr><td className="text-muted">Total Floors</td><td className="text-end"><strong>{listing.totalFloorsInBuilding ?? listing.categorySpecific?.totalFloorsInBuilding ?? "—"}</strong></td></tr>
                              ) : null}
                              {listing.unitNo != null && String(listing.unitNo).trim() !== "" ? (
                                <tr><td className="text-muted">Unit No</td><td className="text-end"><strong>{listing.unitNo}</strong></td></tr>
                              ) : null}
                              {listing.constructionStatus != null && String(listing.constructionStatus).trim() !== "" ? (
                                <tr><td className="text-muted">Construction</td><td className="text-end"><strong>{listing.constructionStatus}</strong></td></tr>
                              ) : null}
                              {listing.furnishingStatus != null && String(listing.furnishingStatus).trim() !== "" ? (
                                <tr><td className="text-muted">Furnishing</td><td className="text-end"><strong>{listing.furnishingStatus}</strong></td></tr>
                              ) : null}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div> */}
                    {/* Additional Property Details from categorySpecific */}
                    <div className="col-12">
                      <div className="card">
                        <div className="card-header bg-secondary text-white">
                          <h6 className="mb-0"><i className="mdi mdi-format-list-bulleted" /> All Property Details</h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            {listing.categorySpecific && Object.entries(listing.categorySpecific)
                              .filter(([key, value]) => {
                                if (value == null || value === "") return false
                                if (key.startsWith("admin_")) return false
                                if (["propertyDescription", "shortPropertyDescription"].includes(key)) return false
                                if (["totalCarpetArea", 'totalSalePrice', "pricePerSqFt"].includes(key)) return false
                                // Filter out document/URL fields - they go to Data Room
                                if (key.toLowerCase().includes("url") || key.toLowerCase().includes("document")) return false
                                // Check if value is a URL string
                                if (typeof value === "string" && (value.startsWith("/uploads") || value.startsWith("http"))) return false
                                return true
                              })
                              .map(([key, value]) => {
                                // Format the value
                                let displayValue: string
                                if (Array.isArray(value)) {
                                  displayValue = value.join(", ")
                                } else if (typeof value === "string") {
                                  // Try to parse JSON array string like '["Basement","Open"]'
                                  try {
                                    const parsed = JSON.parse(value)
                                    if (Array.isArray(parsed)) {
                                      displayValue = parsed.join(", ")
                                    } else {
                                      displayValue = String(value)
                                    }
                                  } catch {
                                    // Check if it looks like an array string
                                    if (value.startsWith("[") && value.endsWith("]")) {
                                      displayValue = value.slice(1, -1).replace(/"/g, "").split(",").map(s => s.trim()).join(", ")
                                    } else {
                                      displayValue = String(value)
                                    }
                                  }
                                } else {
                                  displayValue = String(value)
                                }

                                const areaKeysSqFt = ["plotArea", "superAreaSqFt", "totalCarpetArea", "superArea"]
                                if (areaKeysSqFt.includes(key)) {
                                  displayValue = formatCategoryFieldDisplay(key, value)
                                }

                                const priceRelatedKeys = [
                                  "propertyTaxMunicipalTax", "totalSalePrice", "estimatedMonthlyMaintenance", "pricePerSqFt",
                                  "securityDepositTotal", "currentGrossMonthlyRentalAllTenants", "totalMonthlyMaintenanceCost",
                                  "pricePerKey", "pricePerRoom", "totalPriceOffered", "rentalPerSqFtMonthly", "currentMonthlyRental",
                                  "monthlyMaintenanceCharges", "currentMonthlyRentalAllTenants",
                                ]
                                if (priceRelatedKeys.includes(key)) {
                                  const num = typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""))
                                  if (Number.isFinite(num)) displayValue = formatPrice(num)
                                } else if (!areaKeysSqFt.includes(key) && (typeof value === "number" || (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(String(value).trim().replace(/,/g, ""))))) {
                                  displayValue = formatDisplayNumber(value)
                                }

                                const heightKeysFt = ["ceilingHeight"]
                                if (heightKeysFt.includes(key) && displayValue && displayValue !== "—") {
                                  displayValue = `${displayValue} ft`
                                }

                                return (
                                  <div key={key} className="col-md-4 col-sm-6 mb-2">
                                    <div className="d-flex justify-content-between border-bottom pb-1">
                                      <span className="text-muted text-capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</span>
                                      <strong className="text-end" style={{ maxWidth: "60%" }}>{displayValue}</strong>
                                    </div>
                                  </div>
                                )
                              })}
                          </div>
                        </div>
                      </div>
                    </div>


                  </div>
                )}
              </TabPane>

              {/* Tab 3: Data Room - Login required */}
              <TabPane tabId="dataroom">
                {!isLoggedIn ? (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i className="mdi mdi-lock-outline text-warning" style={{ fontSize: "4rem" }} />
                      <h5 className="mt-3 mb-2">Login Required</h5>
                      <p className="text-muted mb-4">Please login to access the data room.</p>
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={() => navigate("/login", { state: { from: location }, replace: false })}
                      >
                        <i className="mdi mdi-login me-2" /> Login to View Documents
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="data-room-container">
                    {(() => {
                      const propertyImages = listing.propertyImagesUrls || []
                      const docs = (listing.propertyDocumentsUrls?.length
                        ? listing.propertyDocumentsUrls
                        : [
                          listing.generatedFlyerPdfUrl,
                          listing.floorPlanUrl,
                          listing.floorPlanSiteLayoutUrl,
                          listing.buildingBrochureUrl,
                          ...(listing.dataRoomUrls || []),
                        ].filter(Boolean)) as string[]
                      const taxDocs = listing.propertyTaxReceiptsUrls || []
                      const legalDocs = (listing.legalDocumentsUrls?.length
                        ? listing.legalDocumentsUrls
                        : [
                          listing.titleDeedUrl || listing.titleDeedConveyanceDeedUrl,
                          listing.titleDeedSaleDeedUrl,
                          listing.occupancyCertificateOcUrl,
                          listing.possessionCertificateUrl,
                          listing.encumbranceCertificateUrl,
                          listing.leaseAgreementUrl,
                          listing.vastuReportUrl,
                          listing.latestTaxReceiptsUrl,
                        ].filter(Boolean)) as string[]

                      // Group documents from categorySpecific by type
                      const categoryDocsGrouped: Record<string, string[]> = {}
                      if (listing.categorySpecific) {
                        Object.entries(listing.categorySpecific).forEach(([key, value]) => {
                          if (value && typeof value === "string") {
                            if (value.startsWith("/uploads") || (value.includes("/") && (
                              value.endsWith(".pdf") || value.endsWith(".doc") || value.endsWith(".docx") ||
                              value.endsWith(".xls") || value.endsWith(".xlsx") || value.endsWith(".jpg") ||
                              value.endsWith(".jpeg") || value.endsWith(".png") || value.endsWith(".gif")
                            ))) {
                              const label = key.replace(/([A-Z])/g, " $1").replace(/url/gi, "").replace(/urls/gi, "").trim()
                              if (!categoryDocsGrouped[label]) categoryDocsGrouped[label] = []
                              categoryDocsGrouped[label].push(value)
                            }
                          }
                        })
                      }

                      const hasAnyContent = propertyImages.length > 0 || docs.length > 0 || taxDocs.length > 0 ||
                        legalDocs.length > 0 || Object.keys(categoryDocsGrouped).length > 0

                      if (!hasAnyContent) {
                        return (
                          <div className="card">
                            <div className="card-body text-center py-5 text-muted">
                              <i className="mdi mdi-file-document-outline" style={{ fontSize: "4rem" }} />
                              <p className="mt-3 mb-0">No documents uploaded for this property.</p>
                            </div>
                          </div>
                        )
                      }

                      const renderDocumentSection = (title: string, documents: string[], icon: string, bgColor: string) => {
                        if (documents.length === 0) return null
                        const isImageSection = title.toLowerCase().includes("image") || title.toLowerCase().includes("photo")

                        return (
                          <div className="card mb-4" key={title}>
                            <div className={`card-header ${bgColor} text-white d-flex justify-content-between align-items-center`}>
                              <h6 className="mb-0"><i className={`mdi ${icon} me-2`} />{title}</h6>
                              <span className="badge bg-light text-dark">{documents.length} {documents.length === 1 ? "file" : "files"}</span>
                            </div>
                            <div className="card-body">
                              {isImageSection ? (
                                <div className="row g-3">
                                  {documents.map((url, idx) => {
                                    const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`
                                    return (
                                      <div key={idx} className="col-md-6 col-lg-4">
                                        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="d-block">
                                          <img
                                            src={fullUrl}
                                            alt={`Property ${idx + 1}`}
                                            className="img-fluid rounded shadow-sm w-100"
                                            style={{ height: "200px", objectFit: "cover" }}
                                          />
                                        </a>
                                      </div>
                                    )
                                  })}
                                </div>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: "50px" }}>#</th>
                                        <th>File Name</th>
                                        <th style={{ width: "120px" }}>Type</th>
                                        <th style={{ width: "120px" }} className="text-end">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {documents.map((url, idx) => {
                                        const fileName = url.split("/").pop() || `Document ${idx + 1}`
                                        const ext = fileName.split(".").pop()?.toLowerCase() || ""
                                        const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`
                                        const isPdf = ext === "pdf"
                                        const isExcel = ["xls", "xlsx", "csv"].includes(ext)
                                        const isWord = ["doc", "docx"].includes(ext)
                                        const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)

                                        let typeIcon = "mdi-file-document text-secondary"
                                        let typeName = ext.toUpperCase()
                                        if (isPdf) { typeIcon = "mdi-file-pdf-box text-danger"; typeName = "PDF" }
                                        else if (isExcel) { typeIcon = "mdi-file-excel text-success"; typeName = "Excel" }
                                        else if (isWord) { typeIcon = "mdi-file-word text-primary"; typeName = "Word" }
                                        else if (isImage) { typeIcon = "mdi-file-image text-info"; typeName = "Image" }

                                        return (
                                          <tr key={idx}>
                                            <td>{idx + 1}</td>
                                            <td>
                                              <i className={`mdi ${typeIcon} me-2`} />
                                              <span className="text-truncate" style={{ maxWidth: "300px", display: "inline-block", verticalAlign: "middle" }} title={fileName}>
                                                {fileName}
                                              </span>
                                            </td>
                                            <td><span className="badge bg-light text-dark">{typeName}</span></td>
                                            <td className="text-end">
                                              <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                                                <i className="mdi mdi-download me-1" />Download
                                              </a>
                                            </td>
                                          </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      }

                      return (
                        <>
                          {renderDocumentSection("Property Images", propertyImages, "mdi-image-multiple", "bg-primary")}
                          {renderDocumentSection("Property Documents", docs, "mdi-file-document-multiple", "bg-success")}
                          {renderDocumentSection("Tax Receipts", taxDocs, "mdi-receipt", "bg-warning")}
                          {renderDocumentSection("Legal Documents", legalDocs, "mdi-scale-balance", "bg-info")}
                          {Object.entries(categoryDocsGrouped).map(([label, urls]) =>
                            renderDocumentSection(label, urls, "mdi-file-document", "bg-secondary")
                          )}
                        </>
                      )
                    })()}

                    {/* Video Tour if available */}
                    {(() => {
                      const videoUrl = listing.categorySpecific?.admin_propertyVideoUrl || listing.categorySpecific?.videoTourLink
                      const embedUrl = getVideoEmbedUrl(videoUrl as string)
                      if (!embedUrl) return null
                      return (
                        <div className="card mb-4">
                          <div className="card-header bg-danger text-white">
                            <h6 className="mb-0"><i className="mdi mdi-video me-2" />Property Video Tour</h6>
                          </div>
                          <div className="card-body p-0">
                            <div className="ratio ratio-16x9">
                              <iframe
                                src={embedUrl}
                                title="Property Video"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{ border: 0 }}
                              />
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                )}
              </TabPane>
            </TabContent>
          </div>

          <div className="alert alert-warning mb-0">
            <small>
              <i className="mdi mdi-alert-circle" /> <strong>Disclaimer:</strong> All information is provided by the seller and should be independently verified.
              SoldX is not responsible for any discrepancies. Please conduct due diligence before bidding.
            </small>
          </div>
        </div>
      </main>

      <SoldxLandingFooter />

      <Modal
        isOpen={!!lightboxImageUrl}
        toggle={() => setLightboxImageUrl(null)}
        size="xl"
        className="property-detail-lightbox-modal"
        centered
        backdrop
      >
        <ModalHeader toggle={() => setLightboxImageUrl(null)} className="property-detail-lightbox-header">
          View image
        </ModalHeader>
        <ModalBody className="property-detail-lightbox-body">
          {lightboxImageUrl && (
            <img
              src={lightboxImageUrl}
              alt="Full size"
              className="property-detail-lightbox-img"
            />
          )}
        </ModalBody>
      </Modal>

      <BrokerBidBlockedModal
        isOpen={brokerBidBlockedModalOpen}
        toggle={() => setBrokerBidBlockedModalOpen(false)}
        message={brokerBidBlockedMessage}
      />

      <BidRequestModal
        isOpen={bidModalOpen}
        toggle={() => setBidModalOpen(false)}
        listingId={listing._id}
        startingBid={
          initialBidForModal ||
          (userHasBid && biddingDetails?.minNextBid != null
            ? String(biddingDetails.minNextBid)
            : listing.biddingStartPrice != null
              ? String(listing.biddingStartPrice)
              : "0")
        }
        currentHighestBid={biddingDetails?.currentHighestBid?.amount ?? null}
        minBidIncrease={biddingDetails?.listing?.biddingMinIncrement ?? listing?.biddingMinIncrement}
        listingStatus={listing.status}
        reservePrice={listing.reservePrice}
        pricePerSqFt={listing.pricePerSqFt}
        roiPercentage={
          (listing.categorySpecific?.desiredRentalYieldRoiPercent as number | undefined) ??
          (listing as { roiPercentage?: number }).roiPercentage
        }
        superAreaSqFt={(() => {
          const v = listing.categorySpecific?.superArea ?? listing.superAreaSqFt
          if (v == null) return undefined
          const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""))
          return Number.isFinite(n) ? n : undefined
        })()}
        annualRent={(() => {
          const monthly = (listing.categorySpecific?.currentGrossMonthlyRentalAllTenants ?? listing.categorySpecific?.currentMonthlyRental) as number | string | undefined
          if (monthly == null) return undefined
          const m = typeof monthly === "number" ? monthly : parseFloat(String(monthly).replace(/,/g, ""))
          if (!Number.isFinite(m)) return undefined
          return m * 12
        })()}
        propertyTitle={listing.propertyTitle}
        propertyAddress={listing.address || [listing.microMarketLocality, listing.city, listing.state].filter(Boolean).join(", ")}
        auctionDate={listing.auctionStartDate ? formatIndianDate(listing.auctionStartDate) : undefined}
        userHasBid={userHasBid}
        onSuccess={() => {
          setBidModalOpen(false)
          setUserHasBid(true)

          // Refresh bidding details (current highest bid, etc.)
          apiClient
            .get<{ success: boolean; data?: BiddingDetailsData }>(`${BIDS_DETAILS_URL}/${listing._id}`)
            .then((res) => res.data?.success && res.data?.data && setBiddingDetails(res.data.data))
            .catch(() => { })

          // Refresh user's last bid amount
          apiClient
            .get<{ success: boolean; data?: { _id: string; amount?: number }[] }>(`${BIDS_MY_BIDS_URL}?listingId=${listing._id}`)
            .then((res) => {
              if (res.data?.success && Array.isArray(res.data?.data) && res.data.data.length > 0) {
                const bids = res.data.data
                const lastBid = bids.reduce((max, bid) =>
                  (bid.amount ?? 0) > (max.amount ?? 0) ? bid : max
                  , bids[0])
                setUserLastBidAmount(lastBid.amount ?? null)
              }
            })
            .catch(() => { })
        }}
      />
    </div>
  )
}

export default PropertyDetail
